import { css } from '@emotion/react';
import { type ReactNode, memo, useMemo } from 'react';
import { useVirtualScroll } from '../../../hooks/useVirtualScroll';

interface VirtualListProps<T> {
  items: T[];
  itemHeight: number;
  height: number;
  renderItem: (item: T, index: number) => ReactNode;
  emptyComponent?: ReactNode;
}

function VirtualListInner<T>({
  items,
  itemHeight,
  height,
  renderItem,
  emptyComponent,
}: VirtualListProps<T>) {
  const { startIndex, endIndex, totalHeight, handleScroll } = useVirtualScroll({
    itemCount: items.length,
    itemHeight,
    containerHeight: height,
  });

  const containerCss = useMemo(() => containerStyle(height), [height]);
  const innerCss = useMemo(() => innerStyle(totalHeight), [totalHeight]);

  if (items.length === 0) {
    return <>{emptyComponent}</>;
  }

  return (
    <div css={containerCss} onScroll={handleScroll}>
      <div css={innerCss}>
        {Array.from({ length: Math.max(0, endIndex - startIndex + 1) }, (_, i) => {
          const index = startIndex + i;
          if (index < 0 || index >= items.length) {
            return null;
          }
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: index * itemHeight,
                left: 0,
                right: 0,
                height: itemHeight,
              }}
            >
              {renderItem(items[index], index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const VirtualList = memo(VirtualListInner) as typeof VirtualListInner;

const containerStyle = (h: number) => css`
  height: ${h}px;
  overflow-y: auto;
  position: relative;
`;

const innerStyle = (h: number) => css`
  height: ${h}px;
  position: relative;
`;
